// Shared plumbing for the doctor↔patient chat thread — used by both
// DoctorChatInterface (the patient's view) and PatientChatInterface (the
// doctor's view) so the two sides cannot drift apart.
//
// The upload limits are a HAND MIRROR of Backend/config/multerChatConfig.js.
// Without them multer rejects the file only after the whole body has gone over
// the wire, and the browser surfaces that as an opaque 400/413 with no hint of
// which file was wrong. If the backend list changes, change it here as well.

import axios from 'axios';
import baseURL from '../../utils/baseURL';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { formatTime } from '../../utils/clock';

export const MAX_CHAT_FILES = 5;
export const MAX_CHAT_FILE_MB = 10;

// `accept` is only a hint — it lets `image/*` through wholesale, including
// types the backend refuses such as SVG. ALLOWED_MIME_TYPES is the real check.
export const CHAT_FILE_ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip';

const ALLOWED_MIME_TYPES = new Set([
    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'application/zip',
    'application/x-zip-compressed',
    'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/webm', 'audio/x-m4a',
]);

export const isImageAttachment = (f) =>
    f?.fileType === 'image' || String(f?.mimeType || '').startsWith('image/');

export const isVoiceAttachment = (f) =>
    f?.fileType === 'voice' || String(f?.mimeType || '').startsWith('audio/');

// Returns the files that may be sent plus the first problem found, so the
// composer can accept the good ones and still say what was dropped and why.
export function pickValidChatFiles(incoming, alreadySelected, t) {
    const files = Array.from(incoming || []);
    const accepted = [];
    let error = '';

    for (const file of files) {
        if ((alreadySelected?.length || 0) + accepted.length >= MAX_CHAT_FILES) {
            error = error || t('chat.max_files_error', 'You can only upload a maximum of 5 files.');
            break;
        }
        if (file.size / 1024 / 1024 >= MAX_CHAT_FILE_MB) {
            error = error || `${file.name}: ${t('chat.file_too_large', 'File size must be less than 10MB')}`;
            continue;
        }
        if (!ALLOWED_MIME_TYPES.has(file.type)) {
            error = error || `${file.name}: ${t('chat.file_type_not_allowed', 'that file type is not accepted here.')}`;
            continue;
        }
        accepted.push(file);
    }

    return { files: accepted, error };
}

// Attachment URLs arrive presigned and therefore EXPIRE. Downloading through
// the authenticated /chat/download route re-signs on demand, so we send the
// stored object key — the last path segment with any signature query stripped —
// rather than the presigned URL itself. There is no window.open fallback: chat
// attachments are patient data and the route requires a bearer token a browser
// navigation cannot send, so it would only ever render a 401 page.
export async function downloadChatAttachment(fileUrl, fileName) {
    if (!fileUrl) return;
    const key = String(fileUrl).split('?')[0].split('/').pop();
    const { data } = await axios.get(`${baseURL}/chat/download/${encodeURIComponent(key)}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    });
    const objectUrl = window.URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = fileName || key;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(objectUrl);
}

// The bubble's time of day, in the one clock format ("2:30 PM", utils/clock).
export const formatMessageTime = (value) => (value ? formatTime(value) : '');

// ---- Socket refusals ---------------------------------------------------------
//
// The chat socket answers a refused action with `error { code, message, event }`
// (Backend/socket/socketServer.js#chatGate). Those used to reach only the PAGE,
// whose catch-all handler swapped the whole screen for a 500 ErrorPage — the
// doctor list, the thread and the line being typed all gone for what was a
// refusal of ONE message (P26). Now the open thread reads them in place.
//
// Events a thread shows. typing / stopTyping / markAsSeen refusals change
// nothing the reader did, and the socket is shared with Support — a refusal
// naming any other event belongs to somebody else and is ignored.
const THREAD_EVENTS = new Set(['initChat', 'joinRoom', 'fetchHistory', 'sendMessage']);

/**
 * What an open thread should do with a socket `error` payload, or null when it
 * is not the thread's to show.
 *   readOnly    — READ_ONLY_PROFILE: lock the composer and say why.
 *   sendRefused — a message in flight did not land: take its bubble down and
 *                 put its text back in the box. An error naming no event is the
 *                 legacy send path's own catch, so it counts too.
 */
export function readChatSocketError(payload, fallback) {
    if (!payload || typeof payload !== 'object') return null;
    const event = typeof payload.event === 'string' ? payload.event : null;
    if (event && !THREAD_EVENTS.has(event)) return null;
    return {
        event,
        code: payload.code || null,
        readOnly: payload.code === 'READ_ONLY_PROFILE',
        sendRefused: !event || event === 'sendMessage',
        message: extractErrorMessage(payload, fallback),
    };
}

// A thread's own optimistic rows carry a client-side id, which is not a
// messages.id — sending it as replyTo would fail the BIGINT FK server-side.
export const serverMessageId = (value) => (/^\d+$/.test(String(value ?? '')) ? String(value) : null);

// ---- Reactions -------------------------------------------------------------
//
// A HAND MIRROR of Backend/config/chatReactions.js. The server rejects anything
// outside its own list, so a picker offering more would only render buttons that
// fail; if the backend list changes, change it here and in the MOC patient web
// app's components/chat/chatHelpers.js as well.
export const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

// A reaction row identifies its author by the thread-side id + role the server
// stamped it with, which is the same pair this client sends as senderId/role.
const sameReactor = (r, me) =>
    !!r && !!me && String(r.userId) === String(me.userId) && r.userRole === me.userRole;

// The array as the server will have it after `me` taps `emoji` — the same
// toggle/replace rule as applyReaction() in Backend/socket/socketServer.js:
// a different emoji REPLACES your previous one, the same emoji takes it back.
// Kept in step by hand so the optimistic chip already matches the broadcast
// that follows it; if the two rules disagree, every tap visibly flickers.
export function applyReactionLocally(reactions, me, emoji) {
    const list = Array.isArray(reactions) ? reactions : [];
    const mine = list.find((r) => sameReactor(r, me));
    const without = list.filter((r) => !sameReactor(r, me));
    if (mine && mine.emoji === emoji) return without;
    return [...without, { emoji, userId: me.userId, userRole: me.userRole, at: new Date().toISOString() }];
}

// One chip per emoji, ordered by REACTION_EMOJIS and NOT by count — a chip that
// reorders itself when the other party reacts moves out from under the finger
// already on its way down to it.
export function summariseReactions(reactions, me) {
    const list = Array.isArray(reactions) ? reactions : [];
    if (list.length === 0) return [];

    const byEmoji = new Map();
    list.forEach((r) => {
        if (!r || !r.emoji) return;
        const chip = byEmoji.get(r.emoji) || { emoji: r.emoji, count: 0, mine: false };
        chip.count += 1;
        if (sameReactor(r, me)) chip.mine = true;
        byEmoji.set(r.emoji, chip);
    });

    const known = REACTION_EMOJIS.filter((e) => byEmoji.has(e)).map((e) => byEmoji.get(e));
    // A stored emoji that is no longer offered is still someone's reaction —
    // show it after the known ones rather than making their tap disappear.
    const retired = [...byEmoji.values()].filter((c) => !REACTION_EMOJIS.includes(c.emoji));
    return [...known, ...retired];
}

// Which emoji this reader currently holds on a message, for marking the picker.
export function myReaction(reactions, me) {
    const list = Array.isArray(reactions) ? reactions : [];
    return list.find((r) => sameReactor(r, me))?.emoji || null;
}
